/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — 3D Flood Inundation family (#177 Cesium-wow;
 *  operator-cop-doctrine §3A.7, §8.4 colour law; §2.7 flood). 2026-06-08.
 *
 *  The SINGLE source of the modelled-flood render's colours. Consumed ONLY by the
 *  flood-inundation family:
 *    - the FloodInundationLayer translucent water surface
 *    - the flood-blue waterline edge highlight on submerged (flooded) buildings
 *    - the FloodInundationControl legend swatches
 *
 *  Colour law: the flood family is BLUE. We reuse the CAP flood dark-blue (#1e40af)
 *  for the water body so "flood" reads consistently with the Extreme-Weather flood
 *  polygons, a brighter flood-cyan (#38bdf8) for the building waterline edge so the
 *  flooded buildings stand out AGAINST the darker water, and a saturated mid flood-blue
 *  (#2563eb) for the per-building SOLID recolor of FLOOD-AFFECTED low-rise buildings
 *  (#220) — three distinct VALUES of the same blue hue, never a cross-family hue. This
 *  is DISTINCT from the Hydrometric-Gauge
 *  rate-of-rise palette (teal/yellow/orange/violet) — gauges signal "how fast is it
 *  rising", inundation signals "where the water is" — so the two flood layers never
 *  read as the same thing. Colour is never load-bearing alone: the layer is labelled
 *  "(modelled)" and the control legend names the water + the flooded buildings.
 *
 *  ⛔ Do NOT import another family's palette here (enforced by the
 *     no-restricted-imports lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

/** Translucent water-body fill (CAP flood dark-blue, colour law). */
export const FLOOD_WATER_CSS = '#1e40af';
/** Water-surface alpha — translucent so terrain + submerged building geometry read
 *  through it (the "watch the water rise" effect depends on this translucency). */
export const FLOOD_WATER_ALPHA = 0.5;

/** Waterline edge drawn on buildings clipped at the flood surface — the distinct,
 *  colour-law-compliant "this building floods" highlight (brighter flood-cyan so it
 *  separates from the darker water body). */
export const FLOOD_BUILDING_EDGE_CSS = '#38bdf8';
/** Width (px) of the flooded-building waterline edge. */
export const FLOOD_BUILDING_EDGE_WIDTH = 2;

/** A subtle desaturating tint applied to "standing" (taller / not-recolored) demo-
 *  region buildings so the translucent water reads clearly over the OSM Buildings
 *  default white. */
export const FLOOD_BUILDING_TINT_CSS = '#cbd5e1';
export const FLOOD_BUILDING_TINT_ALPHA = 1;

/** Per-building SOLID recolor (#220) for FLOOD-AFFECTED low-rise buildings — a
 *  saturated mid flood-blue so a flooded house reads as a solid blue mass, not just a
 *  thin waterline edge. Within the blue/flood family, a distinct VALUE from both the
 *  darker water body (#1e40af) and the brighter waterline edge (#38bdf8). */
export const FLOOD_BUILDING_FLOODED_CSS = '#2563eb';

/** Legend copy — the honesty framing the prompt + colour law require. */
export const FLOOD_LEGEND = {
  title: '3D Flood Inundation — MODELLED',
  water: 'Modelled water surface (translucent)',
  flooded: 'Flood-affected low-rise buildings (solid)',
  standing: 'Taller buildings standing in the flood (waterline)',
  disclaimer:
    'A modelled what-if for situational rehearsal — NOT an official inundation product. Water level is operator-set or mapped from the upstream gauge (relative datum).',
} as const;
